import BasicRadio, { type Signature as BasicRadioSig } from './basic-radio.gts';
import { hash } from '@ember/helper';
import { type TOC } from '@ember/component/template-only';
import type { WithBoundArgs } from '@glint/template';

interface SubRadioSignature {
  Element: HTMLDivElement;
  Args: Pick<
    BasicRadioSig['Args'],
    | 'propertyName'
    | 'isReadOnly'
    | 'label'
    | 'value'
    | 'current'
    | 'disabled'
    | 'translationKey'
  > & {
    onChange: () => void;
  };
}

const WrappedRadio: TOC<SubRadioSignature> = <template>
  <div ...attributes>
    <BasicRadio
      @propertyName={{@propertyName}}
      @isReadOnly={{@isReadOnly}}
      @label={{@label}}
      @value={{@value}}
      @current={{@current}}
      @onChange={{@onChange}}
      @translationKey={{@translationKey}}
    />
  </div>
</template>;

const FieldRadio: TOC<SubRadioSignature> = <template>
  <div class='field' ...attributes>
    <BasicRadio
      @propertyName={{@propertyName}}
      @isReadOnly={{@isReadOnly}}
      @label={{@label}}
      @value={{@value}}
      @current={{@current}}
      @onChange={{@onChange}}
      @disabled={{@disabled}}
      @translationKey={{@translationKey}}
    />
  </div>
</template>;

export interface Signature {
  Element: HTMLDivElement;
  Args: Pick<
    BasicRadioSig['Args'],
    | 'propertyName'
    | 'isReadOnly'
    | 'current'
    | 'disabled'
    | 'translationKey'
    | 'value'
  > & {
    title?: string;
    isMandatory?: boolean;
    displaySubText?: boolean;
    subText?: string;
    onChange?: () => void;
  };
  Blocks: {
    default: [
      {
        Radio: WithBoundArgs<
          typeof BasicRadio,
          | 'current'
          | 'onChange'
          | 'translationKey'
          | 'propertyName'
          | 'isReadOnly'
          | 'disabled'
          | 'value'
        >;
        WrappedRadio: WithBoundArgs<
          typeof WrappedRadio,
          | 'current'
          | 'onChange'
          | 'translationKey'
          | 'propertyName'
          | 'isReadOnly'
          | 'disabled'
        >;
        FieldRadio: WithBoundArgs<
          typeof FieldRadio,
          | 'current'
          | 'onChange'
          | 'translationKey'
          | 'propertyName'
          | 'isReadOnly'
          | 'disabled'
        >;
      }
    ];
  };
}

const BasicRadioGroupComponent: TOC<Signature> = <template>
  {{#if @title}}
    <label for={{@propertyName}}>
      {{@title}}{{if @isMandatory ' *'}}
    </label>
  {{/if}}

  {{#if @displaySubText}}
    <p class='semeia faded-text-color text-[smaller]' data-test-subtext>
      {{@subText}}
    </p>
  {{/if}}

  {{yield
    (hash
      Radio=(component
        BasicRadio
        current=@current
        onChange=@onChange
        translationKey=@translationKey
        propertyName=@propertyName
        isReadOnly=@isReadOnly
        disabled=@disabled
        value=@value
      )
      WrappedRadio=(component
        WrappedRadio
        current=@current
        onChange=@onChange
        translationKey=@translationKey
        propertyName=@propertyName
        isReadOnly=@isReadOnly
        disabled=@disabled
      )
      FieldRadio=(component
        FieldRadio
        current=@current
        onChange=@onChange
        translationKey=@translationKey
        propertyName=@propertyName
        isReadOnly=@isReadOnly
        disabled=@disabled
      )
    )
  }}
</template>;

export default BasicRadioGroupComponent;
